import { useSelector } from 'react-redux';
import { AbstractWallet } from '@blink/components/src/utils';
import { TransactionReceipt, ethers, parseUnits } from 'ethers';

import { Store } from '@/shared/store';
import AgreementABI from '@/shared/abi/AgreementABI.json';
import IERCAbi from '@/shared/abi/IERCAbi.json';
import MarginAccountABI from '@/shared/abi/MarginAccountABI.json';
import { getSafeContract } from '@/shared/utils/getSafeContract';
import { getSafeSigner } from '@/shared/utils/getSafeSigner';
import { getWalletMap } from '@/shared/utils/getWalletMap';

type Callbacks = {
    /** Will fire on order being placed on blockchain */
    orderPlacedCallback?: () => void;
    /** Will fire on order being completed/failed on blockchain */
    receiptReceivedCallback?: (data: any) => void;
};

type LiquidityProps = Callbacks & {
    amount: string;
    agreementAddress: string;
    tokenAddress: string;
};

type VaultDepositProps = Callbacks & {
    amount: string;
    poolAddress: string;
    marginAccountAddress: string;
    tokenAddress: string;
};

type WithdrawVaultProps = Callbacks & {
    amount: string;
    poolAddress: string;
    marginAccountAddress: string;
    tokenAddress: string;
};

type WithdrawProps = LiquidityProps;

type ResolvedTx = {
    wait: (confirmations: number) => Promise<TransactionReceipt>;
};

const INCREASE_GAS_PRICE_PERCENT = 110n;
const TOTAL_PERCENT = 100n;
// Duration to wait for rpc node to catch up last block updates.
const TX_WAIT_DURATION_MS = 3000;
const TX_NUM_CONFIRMATIONS = 1;
// Number of retries when encountering `ReentrantTransactionInSameBlock` error.
const TX_REENTRANCY_ERROR_RETRY_LIMIT = 10;
// Encoded version of `ReentrantTransactionInSameBlock` error.
const TX_REENTRANCY_ERROR_ENCODED = '0x406d52bb';

function timeout(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

export const useEthers = () => {
    const { address, network, type } = useSelector((state: Store) => ({
        address: state.wallets.active.address,
        type: state.wallets.active.type,
        network: state.wallets.network,
    }));

    const setup = async (contractAddress: string, tokenAddress: string, abi = AgreementABI) => {
        const walletMap = getWalletMap();
        const wallet: AbstractWallet = walletMap.get(type) as AbstractWallet;
        const walletProvider = await wallet.getProvider();

        const signer = await getSafeSigner(walletProvider, network);

        const agreement = getSafeContract(contractAddress, abi, signer as ethers.Signer);
        const token = getSafeContract(tokenAddress, IERCAbi, signer as ethers.Signer);
        return { agreement, token };
    };

    // Wait for the block receipt and also wait for defined
    // delay to make sure rpc node catched up all updates.
    // In case of error of `ReentrantTransactionInSameBlock`
    // retry execution until number of retry limit exceeded.
    const waitRpcConfirmation = async (tx: Promise<ResolvedTx>): Promise<TransactionReceipt> => {
        let resolvedTx: ResolvedTx | null = null;

        let attempt = 0;
        do {
            try {
                resolvedTx = await tx;
                break;
            } catch (e) {
                if (e instanceof Error && e.message.includes(TX_REENTRANCY_ERROR_ENCODED)) {
                    await timeout(TX_WAIT_DURATION_MS);
                } else {
                    throw e;
                }
            }

            attempt++;
        } while (attempt < TX_REENTRANCY_ERROR_RETRY_LIMIT);

        if (!resolvedTx) {
            console.error('Reentrancy error retry limit exceeded');
            throw new Error('Failed to confirm trading transaction');
        }

        return await resolvedTx.wait(TX_NUM_CONFIRMATIONS);
    };

    const deposit = async ({
        amount,
        agreementAddress,
        tokenAddress,
        orderPlacedCallback,
        receiptReceivedCallback,
    }: LiquidityProps) => {
        const { agreement, token } = await setup(agreementAddress, tokenAddress);
        const decimals = await token.decimals();
        const allowance = await token.allowance(address, agreementAddress);
        const amountDecimal = parseUnits(amount.toString(), decimals);

        if (allowance < amountDecimal) {
            if (allowance > 0) {
                await waitRpcConfirmation(token.approve(agreementAddress, 0));
            }

            await waitRpcConfirmation(token.approve(agreementAddress, amountDecimal));
        }

        const receipt = await waitRpcConfirmation(agreement.deposit(amountDecimal));

        orderPlacedCallback?.();

        if (receiptReceivedCallback) {
            receiptReceivedCallback(receipt);
        }
    };

    const withdraw = async ({
        amount,
        agreementAddress,
        tokenAddress,
        orderPlacedCallback,
        receiptReceivedCallback,
    }: WithdrawProps) => {
        const { agreement, token } = await setup(agreementAddress, tokenAddress);
        const decimals = await token.decimals();
        const amountDecimal = parseUnits(amount, decimals);

        const receipt = await waitRpcConfirmation(agreement.withdraw(amountDecimal));

        orderPlacedCallback?.();

        if (receiptReceivedCallback) {
            receiptReceivedCallback(receipt);
        }
    };

    const withdrawVault = async ({
        amount,
        marginAccountAddress,
        poolAddress,
        tokenAddress,
        orderPlacedCallback,
        receiptReceivedCallback,
    }: WithdrawVaultProps) => {
        const walletMap = getWalletMap();
        const wallet: AbstractWallet = walletMap.get(type) as AbstractWallet;
        const walletProvider = await wallet.getProvider();

        const signer = await getSafeSigner(walletProvider, network);

        const marginAccount = getSafeContract(
            marginAccountAddress,
            MarginAccountABI,
            signer as ethers.Signer,
        );

        const { agreement, token } = await setup(poolAddress, tokenAddress);

        const decimals = await token.decimals();
        const amountDecimal = parseUnits(amount, decimals);

        const executeWithdraw = async (amount: number | bigint) => {
            const payload = agreement.interface.encodeFunctionData('withdraw', [amount]);

            const estimatedGas = await marginAccount.execute.estimateGas({
                target: poolAddress,
                value: 0,
                payload,
            });

            const gasLimit = (estimatedGas * INCREASE_GAS_PRICE_PERCENT) / TOTAL_PERCENT;

            return await marginAccount.execute(
                {
                    target: poolAddress,
                    value: 0,
                    payload,
                },
                {
                    gasLimit,
                },
            );
        };

        const receipt = await waitRpcConfirmation(executeWithdraw(amountDecimal));

        orderPlacedCallback?.();

        if (receiptReceivedCallback) {
            receiptReceivedCallback(receipt);
        }
    };

    const depositVault = async ({
        amount,
        poolAddress,
        marginAccountAddress,
        tokenAddress,
        orderPlacedCallback,
        receiptReceivedCallback,
    }: VaultDepositProps) => {
        const walletMap = getWalletMap();
        const wallet: AbstractWallet = walletMap.get(type) as AbstractWallet;
        const walletProvider = await wallet.getProvider();

        const signer = await getSafeSigner(walletProvider, network);

        const marginAccount = getSafeContract(
            marginAccountAddress,
            MarginAccountABI,
            signer as ethers.Signer,
        );

        const { agreement, token } = await setup(poolAddress, tokenAddress);

        const decimals = await token.decimals();
        const allowance = await token.allowance(marginAccountAddress, poolAddress);
        const amountDecimal = parseUnits(amount.toString(), decimals);

        const executeApprove = async (amount: number | bigint) => {
            const payload = token.interface.encodeFunctionData('approve', [poolAddress, amount]);

            const estimatedGas = await marginAccount.execute.estimateGas({
                target: tokenAddress,
                value: 0,
                payload,
            });

            const gasLimit = (estimatedGas * INCREASE_GAS_PRICE_PERCENT) / TOTAL_PERCENT;

            return await marginAccount.execute(
                {
                    target: tokenAddress,
                    value: 0,
                    payload,
                },
                {
                    gasLimit,
                },
            );
        };

        const executeDeposit = async (amount: number | bigint) => {
            const payload = agreement.interface.encodeFunctionData('deposit', [amount]);

            const estimatedGas = await marginAccount.execute.estimateGas({
                target: poolAddress,
                value: 0,
                payload,
            });

            const gasLimit = (estimatedGas * INCREASE_GAS_PRICE_PERCENT) / TOTAL_PERCENT;

            return await marginAccount.execute(
                {
                    target: poolAddress,
                    value: 0,
                    payload,
                },
                {
                    gasLimit,
                },
            );
        };

        if (allowance < amountDecimal) {
            if (allowance > 0) {
                await waitRpcConfirmation(executeApprove(0));
            }

            await waitRpcConfirmation(executeApprove(amountDecimal));
        }

        const receipt = await waitRpcConfirmation(executeDeposit(amountDecimal));

        orderPlacedCallback?.();

        if (receiptReceivedCallback) {
            receiptReceivedCallback(receipt);
        }
    };

    return { deposit, withdraw, depositVault, withdrawVault };
};
